import { Activity, useEffect, useState } from 'react'
import {
  Alert,
  Button,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material'

import PermissionMatrix from './PermissionMatrix'
import fetchWithAuth from '../../utilities/fetchWithAuth'
import { useNavigate } from 'react-router-dom'

const CreateRolePage = () => {
  const [roleData, setRoleData] = useState({
    name: '',
    description: '',
    permissions: [],
  })

  const [permissionOptions, setPermissionOptions] = useState({
    resources: [],
    actions: [],
    scopes: [],
  })

  const [errors, setErrors] = useState({})
  const navigate = useNavigate()

  useEffect(() => {
    const fetchPermissionOptions = async () => {
      try {
        const response = await fetchWithAuth('roles/permission-options')
        if (response && response.data && response.data.data) {
          setPermissionOptions(response.data.data)
        } else {
          console.error('Unexpected API response format:', response)
        }
      } catch (error) {
        console.error('Error fetching permission options:', error)
      }
    }

    fetchPermissionOptions()
  }, [])

  const validate = () => {
    const newErrors = {}

    if (!roleData.name.trim()) {
      newErrors.name = 'Name is required'
    } else if (!/^[a-zA-Z0-9_]+$/.test(roleData.name)) {
      newErrors.name = 'Name can only contain letters, numbers and underscores'
    }

    if (roleData.description && roleData.description.length > 200) {
      newErrors.description = 'Description cannot exceed 200 characters'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleChange = (e) => {
    setRoleData({
      ...roleData,
      [e.target.name]: e.target.value,
    })

    if (errors[e.target.name]) {
      setErrors({
        ...errors,
        [e.target.name]: undefined,
      })
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    if (!validate()) {
      return
    }

    try {
      const response = await fetchWithAuth('roles', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(roleData),
      })

      if (response && response.status === 201) {
        navigate('/roles')
      } else {
        const data = response.data
        if (data && data.errors) {
          const apiErrors = {}
          data.errors.forEach((err) => {
            apiErrors[err.field] = err.message
          })
          setErrors(apiErrors)
        } else if (data && data.message) {
          setErrors({ general: data.message })
        }
      }
    } catch (error) {
      console.error('Error creating role:', error)
      setErrors({ general: 'Failed to create role. Please try again.' })
    }
  }

  return (
    <Paper sx={{ p: 3, mx: 2, my: 4 }}>
      <Typography variant="h6" component="h1" gutterBottom>
        Add New Role
      </Typography>
      <Activity mode={errors.general ? 'visible' : 'hidden'}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {errors.general}
        </Alert>
      </Activity>
      <form onSubmit={handleSubmit}>
        <Stack spacing={3}>
          <TextField
            label="Name"
            name="name"
            value={roleData.name}
            onChange={handleChange}
            fullWidth
            required
            error={!!errors.name}
            helperText={
              errors.name ||
              'Role name can only contain letters, numbers and underscores'
            }
          />

          <TextField
            label="Description"
            name="description"
            value={roleData.description}
            onChange={handleChange}
            fullWidth
            multiline
            rows={2}
            error={!!errors.description}
            helperText={errors.description}
            inputProps={{ maxLength: 200 }}
          />

          <Typography variant="h6" component="h2" gutterBottom>
            Permissions
          </Typography>
          <Alert severity="info">
            Define what actions users with this role can perform on different
            resources.
          </Alert>

          <PermissionMatrix
            permissions={roleData.permissions}
            permissionOptions={permissionOptions}
            onChange={(perms) =>
              setRoleData({ ...roleData, permissions: perms })
            }
          />

          <Button
            variant="contained"
            color="primary"
            type="submit"
            size="large"
          >
            Create Role
          </Button>
        </Stack>
      </form>
    </Paper>
  )
}

export default CreateRolePage
